import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useProject } from '../hooks/useProjects';
import GlobalHeader from '../components/layout/GlobalHeader';
import { useScrollY } from '../hooks/useScrollY';
import { useLenis } from '../hooks/useLenis';
import YouTubeEmbed from '../components/ui/YouTubeEmbed';
import { renderFormattedText, sectionTextStyle } from '../utils/richText';

export default function WritingDetailPage() {
  const { id }               = useParams();
  useLenis();
  const scrollY              = useScrollY();
  const { project, loading } = useProject(id);

  useEffect(() => {
    if (!project) return;
    const prevTitle = document.title;
    document.title = `${project.title} — Son Songhee`;

    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute('content');
    if (metaDesc && project.shortDescription) {
      metaDesc.setAttribute('content', project.shortDescription);
    }

    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc != null) metaDesc.setAttribute('content', prevDesc);
    };
  }, [project]);

  const vh         = typeof window !== 'undefined' ? window.innerHeight : 800;
  const scrollable = typeof window !== 'undefined' ? document.documentElement.scrollHeight - vh : 1;
  const progress   = scrollable > 0 ? Math.min(1, scrollY / scrollable) : 0;
  const barVisible = scrollY > vh * 0.5;
  const bodyRadius = Math.round(Math.max(0, 20 * (1 - Math.min(scrollY / (vh * 0.5), 1))));

  if (loading) {
    return (
      <>
        <GlobalHeader hamburgerOnly heroMode />
        <div className="not-found-wrap">
          <div style={{ color: '#a3a3a3', fontSize: '0.875rem' }}>Loading…</div>
        </div>
      </>
    );
  }

  if (!project) {
    return (
      <>
        <GlobalHeader hamburgerOnly heroMode />
        <div className="not-found-wrap">
          <div className="not-found-inner">
            <p className="not-found-msg">Entry not found</p>
            <Link to="/log" className="btn-primary">Back to Log</Link>
          </div>
        </div>
      </>
    );
  }

  const heroImage = project.images?.[0]?.src ?? null;
  const year      = project.year || '';
  const category  = project.role || 'Writing';
  const tags      = project.tags || [];

  return (
    <>
      <GlobalHeader hamburgerOnly heroMode />

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section
        className="ld-hero"
        style={{ background: project.coverColor || '#0a0a0a' }}
      >
        {heroImage && (
          <div className="ld-hero__bg" style={{ backgroundImage: `url(${heroImage})` }} />
        )}
        <div className="ld-hero__gradient" />
        <div className="ld-hero__foot">
          <div className="ld-wrap">
            <div className="ld-meta-row">
              {year && <span className="ld-meta-text">{year}</span>}
              {year && category && <span className="ld-meta-sep">·</span>}
              <span className="ld-meta-text">{category}</span>
            </div>
            <h1 className="ld-title">{project.title}</h1>
            {tags.length > 0 && (
              <div className="ld-tags">
                {tags.map(t => <span key={t} className="ld-tag">{t}</span>)}
              </div>
            )}
            {project.shortDescription && <p className="ld-desc">{project.shortDescription}</p>}
          </div>
        </div>
      </section>

      {/* ── Body ─────────────────────────────────────────────────────── */}
      <div
        className="ld-body"
        style={{ borderRadius: `${bodyRadius}px ${bodyRadius}px 0 0` }}
      >
        <div className="ld-wrap">
          {(project.sections || []).map((sec, i) => (
            <div key={i} className="ld-section">
              <span className="ld-section__label" style={sectionTextStyle(sec)}>{sec.title}</span>
              {sec.content && (
                <p className="ld-section__text" style={sectionTextStyle(sec)}>{renderFormattedText(sec.content)}</p>
              )}
              {sec.videoUrl && <YouTubeEmbed url={sec.videoUrl} />}
              {sec.images && sec.images.length > 0 && (
                <div className="ld-section__imgs">
                  {sec.images.map((img, j) => (
                    <img key={j} src={img.src || img.url || img} alt={img.alt || ''} className="ld-section__img" loading="lazy" />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Bottom bar ───────────────────────────────────────────────── */}
      <div className={`ld-bottom${barVisible ? ' ld-bottom--visible' : ''}`}>
        <div className="ld-progress" style={{ width: `${progress * 100}%` }} />
        <div className="ld-wrap ld-bottom__inner">
          <p className="ld-bottom__title">{project.title}</p>
          <Link to="/log" className="ld-back-btn">← All Log</Link>
        </div>
      </div>

      <Link to="/contact" className="float-contact float-contact--raised" aria-label="Contact">
        Contact
      </Link>
    </>
  );
}
